/**
 * ReadinessCard — daily readiness (0-100) on the dashboard.
 *
 * The score has existed since Phase 20 but only ever reached the coach prompt;
 * this is the first place the athlete sees it. Endpoint: GET /api/recovery/today
 * (app/api/recovery.py, mounted app/main.py:129). No parameters — the server
 * derives the athlete's "today" from User.timezone, so the client must not send
 * a date.
 *
 * Two deliberate behaviours, both about not lying with a number:
 *
 * 1. The headline driver is ranked by the component `penalty`, NOT by the
 *    server's `dominant_concern`. `dominant_concern` is simply concerns[0],
 *    which is first in evaluation order (HRV → sleep → RHR → Body Battery →
 *    stress → training, recovery_score.py:238) — so a −25 sleep deficit would
 *    otherwise be headlined by a −15 HRV dip whenever both fire.
 *
 * 2. All driver copy is composed here from `components`, and the server's
 *    pre-rendered `concerns[].message` is only a fallback. Only 4 of the ~9
 *    server messages have translations (recovery_score.py:15-76); the rest are
 *    hardcoded English regardless of the athlete's language.
 *
 * The number is a morning read: it is computed from the most recent wellness
 * record at or before yesterday, and from yesterday's TSS (coach_context.py:
 * 464-505). The footer says so rather than implying a live measurement.
 */
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { HeartPulse, Watch } from "lucide-react";
import { Card, SectionLabel } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { api } from "@/api/client";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------ types */

type ReadinessTier = "excellent" | "good" | "compromised" | "poor" | "critical";

interface ReadinessConcern {
  type: string;
  message: string;
}

/** Mirrors RecoveryToday (app/api/recovery.py:35-42). `components` is exactly
 *  compute_recovery_score's dict, so every entry is either {missing: true} or a
 *  small bag of numbers — hence the unknown-valued record plus narrowing below
 *  rather than a per-key union the backend does not actually guarantee. */
interface ReadinessToday {
  /** How many wellness signals actually had a value, and how many were
   *  obtainable at all. Supplied by the endpoint so the card never claims more
   *  certainty than the data supports. */
  signals_available: number;
  signals_total: number;
  available: boolean;
  score: number | null;
  tier: ReadinessTier | null;
  dominant_concern: string | null;
  concerns: ReadinessConcern[];
  components: Record<string, Record<string, unknown>>;
}

async function getReadinessToday(): Promise<ReadinessToday> {
  const { data } = await api.get<ReadinessToday>("/recovery/today");
  return data;
}

/* ------------------------------------------------------------- constants */

/** Wellness inputs that may appear in `components`. Body battery and stress are
 *  no longer among them: intervals.icu does not send either, so the backend
 *  stopped reporting them at all rather than showing two permanent gaps.
 *  Coverage itself comes from the response — see below — this list only orders
 *  the driver rows. */
const SIGNAL_KEYS = ["hrv", "sleep", "rhr"] as const;

/** Every scoring component, in the order compute_recovery_score evaluates them. */
const DRIVER_KEYS = [...SIGNAL_KEYS, "yesterday_training"] as const;

type DriverKey = (typeof DRIVER_KEYS)[number];

/** concerns[].type → components key. The two differ for body battery. */
// concerns[].type does not match the components{} keys — "training" maps to
// "yesterday_training". Body battery and stress are absent from both now:
// intervals.icu never sends them, so the backend stopped reporting them.
const CONCERN_TYPE_TO_KEY: Record<string, DriverKey> = {
  hrv: "hrv",
  sleep: "sleep",
  rhr: "rhr",
  training: "yesterday_training",
};

interface Driver {
  key: DriverKey;
  penalty: number;
  label: string;
  detail: string;
}

/* --------------------------------------------------------------- helpers */

function numField(
  component: Record<string, unknown> | undefined,
  field: string,
): number | null {
  const v = component?.[field];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function isPresent(component: Record<string, unknown> | undefined): boolean {
  return !!component && component.missing !== true;
}

/** U+2212 minus, matching the backend's own message formatting. */
function signedPct(v: number): string {
  return `${v < 0 ? "−" : "+"}${Math.abs(v).toFixed(1)}%`;
}

function signedInt(v: number): string {
  if (v === 0) return "0";
  return `${v < 0 ? "−" : "+"}${Math.abs(v)}`;
}

const TIER_STYLES: Record<
  ReadinessTier,
  { score: string; chip: string; bar: string }
> = {
  excellent: {
    score: "text-navy-deep",
    chip: "bg-amber text-navy-deep",
    bar: "bg-amber",
  },
  good: {
    score: "text-navy-deep",
    chip: "bg-amber text-navy-deep",
    bar: "bg-amber",
  },
  compromised: {
    score: "text-amber-dark",
    chip: "bg-cream text-amber-dark border border-cream-border",
    bar: "bg-amber-dark",
  },
  poor: {
    score: "text-coral",
    chip: "bg-coral text-cream-light",
    bar: "bg-coral",
  },
  critical: {
    score: "text-coral",
    chip: "bg-coral text-cream-light",
    bar: "bg-coral",
  },
};

/* ------------------------------------------------------------- component */

interface Props {
  className?: string;
}

export function ReadinessCard({ className }: Props = {}) {
  const { t } = useTranslation();

  const q = useQuery({
    queryKey: ["recovery-today"],
    queryFn: getReadinessToday,
    // Recomputed only when the watch syncs; a stale read costs nothing and a
    // refetch storm on every dashboard mount costs a DB scan per signal.
    staleTime: 5 * 60_000,
    // The score only changes when new wellness data lands, which happens on
    // the hourly sync or a manual one — so refetch when the athlete comes
    // back to the tab rather than leaving a stale number for five minutes
    // after they pressed Sync now.
    refetchOnWindowFocus: true,
  });

  const title = t("readiness.title", "Readiness");

  /* ---- loading ---- */
  if (q.isLoading) {
    return (
      <Card className={className} data-testid="readiness-card">
        <SectionLabel>{title}</SectionLabel>
        <div className="mt-3 space-y-2">
          <Skeleton className="h-9 w-24" label={t("readiness.loading", "Loading readiness")} />
          <Skeleton className="h-1.5 w-full" label={t("readiness.loading", "Loading readiness")} />
          <Skeleton className="h-3 w-4/5" label={t("readiness.loading", "Loading readiness")} />
        </div>
      </Card>
    );
  }

  /* ---- error ----
     TanStack Query v5 dropped the useQuery onError callback, so the error path
     is this branch: it is stated, not swallowed, and it offers the retry. */
  if (q.isError || !q.data) {
    return (
      <Card className={className} data-testid="readiness-card">
        <SectionLabel>{title}</SectionLabel>
        <EmptyState
          variant="inline"
          className="mt-3"
          title={t("readiness.error_title", "Readiness could not be loaded")}
          description={t(
            "readiness.error_desc",
            "The score is still there — this screen just could not reach it.",
          )}
        />
        <Button
          variant="ghost"
          size="sm"
          className="mt-3"
          loading={q.isFetching}
          onClick={() => {
            void q.refetch();
          }}
        >
          {t("readiness.retry", "Try again")}
        </Button>
      </Card>
    );
  }

  const data = q.data;
  const components = data.components ?? {};
  const concerns = data.concerns ?? [];

  // From the endpoint, not recomputed. It already excludes yesterday_training
  // and already knows which signals this athlete's device can supply; deriving
  // it a second time here is how the two would disagree after the next change.
  const signalsPresent = data.signals_available;
  const signalsTotal = data.signals_total;

  /* ---- empty ----
     `available: false` covers both "no wellness record at all" and "a record
     exists but every signal on it is missing". Either way there is nothing to
     put a number on: the penalty model starts at 100 and only subtracts, so an
     unsynced watch would score a confident 100/excellent computed from nothing.
     A new athlete sees this state, and it is the one that must not lie. */
  if (!data.available || data.score === null || data.tier === null) {
    return (
      <Card className={className} data-testid="readiness-card">
        <SectionLabel>{title}</SectionLabel>
        <EmptyState
          variant="hero"
          className="!py-6"
          icon={Watch}
          title={t("readiness.empty_title", "No readiness read yet")}
          description={t(
            "readiness.empty_desc",
            "Readiness needs at least one night from your watch — HRV, sleep or resting heart rate. It appears after the next sync.",
          )}
        >
          <Link
            to="/settings"
            className="text-[12px] font-medium text-amber-dark hover:underline"
          >
            {t("readiness.empty_cta", "Check your watch connection")}
          </Link>
        </EmptyState>
      </Card>
    );
  }

  /* ---- populated ---- */
  const score = data.score;
  const tier = data.tier;
  const styles = TIER_STYLES[tier] ?? TIER_STYLES.good;

  const labelFor = (key: DriverKey): string => {
    switch (key) {
      case "hrv":
        return t("readiness.signal_hrv", "HRV");
      case "sleep":
        return t("readiness.signal_sleep", "Sleep");
      case "rhr":
        return t("readiness.signal_rhr", "Resting heart rate");
      case "yesterday_training":
        return t("readiness.signal_training", "Yesterday's load");
    }
  };

  /** Compose the human sentence from the numbers. Returns null when the shape
   *  is not what we expect, in which case the caller falls back to the server's
   *  pre-rendered message. */
  const detailFor = (key: DriverKey): string | null => {
    const c = components[key];
    switch (key) {
      case "hrv": {
        const value = numField(c, "value");
        if (value === null) return null;
        const deltaPct = numField(c, "delta_pct");
        // delta_pct is already a percentage (−12.4 means 12.4% below baseline).
        if (deltaPct === null) {
          return t("readiness.driver_hrv_plain", "HRV {{value}} ms", {
            value: Math.round(value),
          });
        }
        return t(
          "readiness.driver_hrv",
          "HRV {{value}} ms, {{delta}} against your 28-day baseline",
          { value: Math.round(value), delta: signedPct(deltaPct) },
        );
      }
      case "sleep": {
        const hours = numField(c, "hours");
        if (hours === null) return null;
        return t(
          "readiness.driver_sleep",
          "{{hours}} h of sleep on the last night recorded",
          { hours: hours.toFixed(1) },
        );
      }
      case "rhr": {
        const value = numField(c, "value");
        const delta = numField(c, "delta");
        if (value === null) return null;
        if (delta === null) {
          return t("readiness.driver_rhr_plain", "Resting heart rate {{value}} bpm", {
            value: Math.round(value),
          });
        }
        return t(
          "readiness.driver_rhr",
          "Resting heart rate {{value}} bpm, {{delta}} bpm against your 28-day baseline",
          { value: Math.round(value), delta: signedInt(Math.round(delta)) },
        );
      }
      case "yesterday_training": {
        const tss = numField(c, "tss");
        if (tss === null) return null;
        return t("readiness.driver_training", "{{tss}} TSS yesterday, still to pay back", {
          tss: Math.round(tss),
        });
      }
    }
  };

  const fallbackFor = (key: DriverKey): string | null =>
    concerns.find((c) => CONCERN_TYPE_TO_KEY[c.type] === key)?.message ?? null;

  // Everything that actually cost points, heaviest first. Array.sort is stable,
  // so ties keep the backend's evaluation order.
  const drivers: Driver[] = DRIVER_KEYS.map((key): Driver | null => {
    const c = components[key];
    if (!isPresent(c)) return null;
    const penalty = numField(c, "penalty");
    if (penalty === null || penalty <= 0) return null;
    const detail = detailFor(key) ?? fallbackFor(key);
    if (!detail) return null;
    return { key, penalty, label: labelFor(key), detail };
  })
    .filter((d): d is Driver => d !== null)
    .sort((a, b) => b.penalty - a.penalty);

  const topDriver: Driver | undefined = drivers[0];
  const restDrivers: Driver[] = drivers.slice(1);

  // The one threshold here with clinical meaning: a sustained +7 bpm resting HR
  // is the classic infection / overreaching flag (recovery_score.py, −15 band).
  const rhrDelta = isPresent(components.rhr) ? numField(components.rhr, "delta") : null;
  const illnessFlag = rhrDelta !== null && rhrDelta > 7;

  // ≥50 is "compromised"; at that tier and below the coach stops asking for a
  // calorie deficit, so it is the number with a downstream consequence.
  const constrained = tier === "compromised" || tier === "poor" || tier === "critical";

  const tierLabel =
    tier === "excellent"
      ? t("readiness.tier_excellent", "Excellent")
      : tier === "good"
        ? t("readiness.tier_good", "Good")
        : tier === "compromised"
          ? t("readiness.tier_compromised", "Compromised")
          : tier === "poor"
            ? t("readiness.tier_poor", "Poor")
            : t("readiness.tier_critical", "Critical");

  return (
    <Card className={className} data-testid="readiness-card">
      <div className="flex items-center justify-between">
        <SectionLabel>{title}</SectionLabel>
        <HeartPulse className="h-3.5 w-3.5 text-taupe" strokeWidth={1.8} aria-hidden="true" />
      </div>

      <div className="mt-3 flex items-end gap-2">
        <span
          className={cn("metric-number text-[40px] leading-none", styles.score)}
          aria-label={t("readiness.score_aria", "Readiness {{score}} out of 100", { score })}
        >
          {score}
        </span>
        <span className="mb-1 text-[11px] text-taupe">
          {t("readiness.out_of_100", "/ 100")}
        </span>
        <span
          className={cn(
            "mb-1 ml-auto rounded-md px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.12em]",
            styles.chip,
          )}
          data-testid="readiness-tier"
        >
          {tierLabel}
        </span>
      </div>

      <div
        className="mt-3 h-1.5 overflow-hidden rounded-full bg-cream-border"
        role="presentation"
      >
        <div
          className={cn("h-full rounded-full transition-all", styles.bar)}
          style={{ width: `${Math.max(0, Math.min(100, score))}%` }}
        />
      </div>

      {/* The reason, not the number. */}
      {topDriver ? (
        <p className="mt-3 text-[13px] leading-snug text-navy-deep" data-testid="readiness-driver">
          {t("readiness.driven_by", "Driven by {{driver}}", { driver: topDriver.detail })}
        </p>
      ) : (
        <p className="mt-3 text-[13px] leading-snug text-taupe">
          {t(
            "readiness.nothing_flagged",
            "Nothing is dragging on you — every signal that reported sits inside its normal band.",
          )}
        </p>
      )}

      {illnessFlag ? (
        <p className="mt-1 text-[12px] font-medium leading-snug text-coral">
          {t(
            "readiness.rhr_illness",
            "A resting-heart-rate rise this size is the classic illness or overreaching flag.",
          )}
        </p>
      ) : null}

      {restDrivers.length > 0 ? (
        <ul className="mt-3 space-y-1 border-t border-cream-border pt-2">
          {restDrivers.map((d) => (
            <li
              key={d.key}
              className="flex items-baseline justify-between gap-2 text-[12px]"
              title={`${d.label} — ${d.detail}`}
            >
              <span className="min-w-0 truncate text-taupe">{d.detail}</span>
              <span className="metric-number shrink-0 text-[12px] text-amber-dark">
                {"−"}
                {d.penalty}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {constrained ? (
        <p className="mt-3 rounded-md bg-cream px-3 py-1.5 text-[12px] leading-snug text-navy-deep">
          {t(
            "readiness.constrained_note",
            "At this level your coach will not ask you to run a calorie deficit today.",
          )}
        </p>
      ) : null}

      <p className="mt-3 border-t border-cream-border pt-2 text-[11px] leading-snug text-taupe">
        {t(
          "readiness.window_note",
          "Read from your most recent recovery data and yesterday's training load.",
        )}
        {signalsPresent < signalsTotal ? (
          <>
            {" "}
            {t("readiness.coverage", "Based on {{n}} of {{total}} signals — the rest had not synced.", {
              n: signalsPresent,
              total: signalsTotal,
            })}
          </>
        ) : null}
      </p>
    </Card>
  );
}
